import type { CalendarDate } from '@internationalized/date';
import { getWeeksInMonth } from '@internationalized/date';
import { useCalendarGrid } from '@react-aria/calendar';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import * as React from 'react';
import { useLocale } from 'react-aria';
import { CalendarCell } from './calendar-cell';

type CalendarGridProps = {
	state: CalendarState | RangeCalendarState;
};

const WEEKDAY_STYLE = 'short' as const;

export const CalendarGrid: React.VoidFunctionComponent<CalendarGridProps> = ({ state }) => {
	const { locale } = useLocale();
	const { gridProps, headerProps, weekDays } = useCalendarGrid({ weekdayStyle: WEEKDAY_STYLE }, state);

	const weekIndices: number[] = React.useMemo(() => {
		const weeksInMonth: number = getWeeksInMonth(state.visibleRange.start, locale);

		const weekIndices: number[] = [];
		for (let i = 0; i < weeksInMonth; i++) {
			weekIndices.push(i);
		}

		return weekIndices;
	}, [state.visibleRange.start, locale]);

	const startOfMonth: CalendarDate = state.visibleRange.start;

	return (
		<table {...gridProps} className="w-full border-separate border-spacing-y-1">
			<thead {...headerProps}>
				<tr>
					{weekDays.map((day, index) => (
						<th key={`weekday-${index}`} className="pb-1 text-xs font-medium text-textMediumGray uppercase" scope="col">
							{day}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{weekIndices.map(weekIndex => (
					<tr key={`week-${weekIndex}`}>
						{state.getDatesInWeek(weekIndex).map((date, dayIndex) => {
							if (!date) {
								return <td key={`empty-${weekIndex}-${dayIndex}`} className="p-0" />;
							}

							return <CalendarCell key={date.toString()} state={state} date={date} currentMonth={startOfMonth} />;
						})}
					</tr>
				))}
			</tbody>
		</table>
	);
};
